import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type CreateOrgBody, type OrgRecord, useApiMutation, useOrg } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Check, ChevronsUpDown, Plus, Search, Settings, X } from "lucide-react";
import { Dialog, Popover } from "radix-ui";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

const MAX_ORG_NAME = 64;
const initial = (name: string) => name.trim().charAt(0).toUpperCase() || "?";

export function OrgSwitcher({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const { activeOrg, memberships, setActiveOrg, isLoading, refresh } = useOrg();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingActiveOrgId, setPendingActiveOrgId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const resultRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const navigate = useNavigate();
  const filtered = memberships.filter((org) =>
    org.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );

  useEffect(() => {
    if (pendingActiveOrgId && memberships.some((org) => org.org_id === pendingActiveOrgId)) {
      setActiveOrg(pendingActiveOrgId);
      setPendingActiveOrgId(null);
      navigate("/");
      onNavigate?.();
    }
  }, [pendingActiveOrgId, memberships, setActiveOrg, navigate, onNavigate]);

  function changeOpen(next: boolean) {
    setOpen(next);
    setQuery("");
  }

  function moveThroughResults(event: KeyboardEvent) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const index = resultRefs.current.findIndex((button) => button === event.target);
    if (event.target !== searchRef.current && index === -1) return;
    event.preventDefault();
    if (!filtered.length) return;
    const next =
      event.key === "ArrowDown" ? index + 1 : index === -1 ? filtered.length - 1 : index - 1;
    if (next < 0 || next >= filtered.length) searchRef.current?.focus();
    else resultRefs.current[next]?.focus();
  }

  if (isLoading)
    return (
      <output className="org-trigger org-loading" aria-label="Loading organizations">
        <span className="org-emblem" />
        <span className="org-loading-lines" />
      </output>
    );
  if (!activeOrg) return <div className="org-trigger org-empty">No organization yet</div>;

  return (
    <>
      <Popover.Root open={open} onOpenChange={changeOpen}>
        <Popover.Trigger asChild>
          <button
            ref={triggerRef}
            type="button"
            className="org-trigger"
            aria-label={`Switch organization, ${activeOrg.name}`}
          >
            <span className="org-emblem" aria-hidden="true">
              {initial(activeOrg.name)}
            </span>
            <span className="org-trigger-copy">
              <strong>{activeOrg.name}</strong>
              <span>Your workspace</span>
            </span>
            <ChevronsUpDown size={16} className="switcher-chevron" aria-hidden="true" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="identity-popover org-panel"
            align="start"
            sideOffset={8}
            collisionPadding={12}
            aria-label="Switch organization"
            onKeyDown={moveThroughResults}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              searchRef.current?.focus();
            }}
            onCloseAutoFocus={(event) => {
              if (creating) event.preventDefault();
            }}
          >
            <div className="org-panel-heading">
              <span className="identity-label">Your organizations</span>
              <span className="org-count">{memberships.length}</span>
            </div>
            <div className="org-search">
              <Search size={16} aria-hidden="true" />
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Find an organization"
                placeholder="Find an organization…"
                autoComplete="off"
                spellCheck={false}
              />
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setQuery("");
                    searchRef.current?.focus();
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="org-results" aria-label="Organizations">
              {filtered.map((org, index) => (
                <button
                  type="button"
                  className="org-option"
                  key={org.org_id}
                  ref={(element) => {
                    resultRefs.current[index] = element;
                  }}
                  aria-pressed={org.org_id === activeOrg.org_id}
                  onClick={() => {
                    if (org.org_id !== activeOrg.org_id) {
                      setActiveOrg(org.org_id);
                      navigate("/");
                      onNavigate?.();
                    }
                    changeOpen(false);
                  }}
                >
                  <span className="org-emblem" aria-hidden="true">
                    {initial(org.name)}
                  </span>
                  <span className="identity-copy">
                    <strong>{org.name}</strong>
                    <span className="org-role">{org.role}</span>
                  </span>
                  {org.org_id === activeOrg.org_id && (
                    <Check size={16} className="org-selected" aria-hidden="true" />
                  )}
                </button>
              ))}
              {!filtered.length && (
                <output className="org-no-results">
                  No organizations found.<span>Try a different name.</span>
                </output>
              )}
            </div>
            <div className="identity-footer">
              <Link
                to="/settings/organization"
                className="identity-action"
                onClick={() => {
                  changeOpen(false);
                  onNavigate?.();
                }}
              >
                <Settings size={17} aria-hidden="true" />
                <span>Organization settings</span>
              </Link>
              <button
                type="button"
                className="identity-action create-org-action"
                onClick={() => {
                  setCreating(true);
                  changeOpen(false);
                }}
              >
                <Plus size={17} aria-hidden="true" />
                <span>New organization</span>
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {creating && (
        <CreateOrgModal
          onReturnFocus={() => triggerRef.current?.focus()}
          onClose={() => {
            setCreating(false);
            triggerRef.current?.focus();
          }}
          onCreated={(record) => {
            setPendingActiveOrgId(record.org_id);
            refresh();
            setCreating(false);
            triggerRef.current?.focus();
          }}
        />
      )}
    </>
  );
}
function CreateOrgModal({
  onClose,
  onCreated,
  onReturnFocus,
}: {
  onClose: () => void;
  onCreated: (record: OrgRecord) => void;
  onReturnFocus: () => void;
}) {
  const { pushToast } = useToast();
  const [name, setName] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const create = useApiMutation<CreateOrgBody, OrgRecord>((c, body) => c.orgs.create(body), {
    suppressToast: true,
    onSuccess: (record) => {
      pushToast({ kind: "success", title: `Created ${record.name}` });
      onCreated(record);
    },
    onError: (err) => setServerError(err.message),
  });

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_ORG_NAME && !create.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setServerError(null);
    create.mutate({ name: trimmed });
  }

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="org-dialog-overlay" />
        <Dialog.Content
          className="org-dialog"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onReturnFocus();
          }}
        >
          <span className="org-dialog-icon">
            <Plus size={22} aria-hidden="true" />
          </span>
          <Dialog.Title className="mt-5 text-xl font-medium text-foreground">
            Create organization
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
            Members, projects, secrets, and billing all live under an organization. You become its
            owner.
          </Dialog.Description>
          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                name
              </span>
              <Input
                ref={inputRef}
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setServerError(null);
                }}
                maxLength={MAX_ORG_NAME}
                placeholder="Acme"
                autoComplete="organization"
                aria-invalid={serverError !== null}
                className="mt-2"
              />
              {serverError && (
                <span className="mt-1.5 block font-mono text-[11px] text-destructive">
                  {serverError}
                </span>
              )}
            </label>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={create.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!canSubmit}>
                {create.isPending ? "Creating…" : "Create organization"}
              </Button>
            </div>
          </form>
          <Dialog.Close className="org-dialog-close" aria-label="Close">
            <X size={18} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
